import type { ChangeEvent } from 'react';
import { useMemo, useRef, useState } from 'react';
import {
  handleImageUpload,
  removeImageById,
  createImagePreviews,
  type ReviewImageFile,
} from '@skin-seoul/service/src/utils/review/imageUtils';
import { convertHeicToJpeg } from '../../../utils/image';
import { Section } from './common/SharedStyles';
import { SPACING } from './common/constants';
import {
  renderProductIntro,
  renderRatingSection,
  renderSkinInfoSection,
  renderReviewContentSection,
  renderTitleSection,
  renderImageUploadSection,
  renderRecommendationSection,
  renderNicknameSection,
  renderEmailSection,
  renderTermsSection,
  renderSubmitSection,
  ReviewRewardBanner,
} from './common/formHelpers';
import type { ReviewFormProps } from '@skin-seoul/service';

const MobileSection = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <Section style={{ marginBottom: SPACING['2xl'], ...style }}>{children}</Section>
);

interface ReviewWriteFormProps extends ReviewFormProps {
  variant: 'mobile' | 'pc';
}

export const ReviewWriteForm = ({
  formData,
  formActions,
  options,
  productInfo,
  isSubmitDisabled,
  isMember,
  onSubmit,
  variant,
}: ReviewWriteFormProps) => {
  const images = formData.images as ReviewImageFile[];
  const [isProcessingImages, setIsProcessingImages] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  const isConvertingRef = useRef(false);

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    // `handleImageUpload` appends to the `images` captured by this closure, so two overlapping runs
    // would both start from the same list and the slower one would overwrite the faster one's
    // photos. Serialise on a ref rather than `isProcessingImages`: state reads can lag behind a
    // second change event, a ref flips synchronously.
    if (isConvertingRef.current) {
      e.target.value = '';
      return;
    }

    isConvertingRef.current = true;
    setImageUploadError(null);
    setIsProcessingImages(true);

    try {
      await handleImageUpload(e, images, formActions.setImages as (images: ReviewImageFile[]) => void, {
        convertHeicToJpeg,
        onError: setImageUploadError,
      });
    } finally {
      isConvertingRef.current = false;
      setIsProcessingImages(false);
    }
  };

  const handleRemoveImage = (id: string) => {
    removeImageById(id, images, formActions.setImages as (images: ReviewImageFile[]) => void);
  };

  const imagePreviews = useMemo(() => createImagePreviews(images), [images]);

  const SectionComponent = variant === 'mobile' ? MobileSection : undefined;

  return (
    <>
      <div className="w-full py-4">
        <ReviewRewardBanner />
      </div>
      {renderProductIntro({ productInfo })}
      <div className="w-full h-px bg-neutral-100 shrink-0 my-4 tablet:my-8" />
      <div className="flex flex-col gap-10 tablet:gap-6 px-[2px] tablet:px-0 items-start w-full">
        {renderRatingSection({
          rating: formData.rating,
          setRating: formActions.setRating,
          SectionComponent,
        })}
        {renderTitleSection({
          title: formData.title,
          setTitle: formActions.setTitle,
        })}
        {renderReviewContentSection({
          content: formData.content,
          setContent: formActions.setContent,
        })}
        {renderImageUploadSection({
          images,
          imagePreviews,
          handleImageChange,
          handleRemoveImage,
          isProcessingImages,
          imageUploadError,
        })}
        {renderRecommendationSection({
          recommend: formData.recommend,
          setRecommend: formActions.setRecommend,
        })}
        {renderSkinInfoSection({
          skinType: formData.skinType,
          setSkinType: formActions.setSkinType,
          skinConcern: formData.skinConcern,
          setSkinConcern: formActions.setSkinConcern,
          skinTypeOptions: options.skinTypeOptions,
          skinConcernOptions: options.skinConcernOptions,
        })}
        {!isMember &&
          renderNicknameSection({
            nickname: formData.nickname,
            setNickname: formActions.setNickname,
          })}
        {!isMember &&
          renderEmailSection({
            email: formData.email,
            setEmail: formActions.setEmail,
          })}
        {renderTermsSection()}
        {/* Photos are attached to `formData.images` only once conversion resolves, so submitting
            mid-conversion would post the review without the photo the visitor just picked. */}
        {renderSubmitSection({ isSubmitDisabled: isSubmitDisabled || isProcessingImages, onSubmit })}
      </div>
    </>
  );
};
